import { ButtonSpinner } from "@/components/ButtonSpinner";
import { AccreditationPortalLayout } from "@/components/layouts/AccreditationPortalLayout";
import { Documents } from "@/components/portal/accreditation/Documents";
import ManageGroupHelpDrawer from "@/components/portal/accreditation/ManageGroupHelpDrawer";
import { ManageGroupNav } from "@/components/portal/accreditation/ManageGroupNav";
import { TicketAllocationDropdown } from "@/components/portal/accreditation/TicketAllocationDropdown";
import { PortalLayout } from "@/components/portal/layouts/PortalLayout";
import { Logo } from "@/components/portal/Logo";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dropdown } from "@/crud/form/Dropdown";
import { Input } from "@/crud/form/Input";
import yup from "@/crud/yup-extended";
import { BASE_URL } from "@/dashboardQueries";
import { useDarkMode } from "@/hooks";
import {
  AccreditationIndividual,
  ManageAccreditationGroup,
  ManageAccreditationGroupRequest,
  TicketAllocation,
} from "@/models/accreditation";
import { DropdownOption } from "@/models/Dashboard";
import { usePortalStore } from "@/store/portal";
import { classNames } from "@/utils/styles";
import {
  ChevronDownIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import axios, { all } from "axios";
import { use } from "chai";
import { is } from "cypress/types/bluebird";
import {
  Field,
  FieldArray,
  Formik,
  FormikValues,
  getIn,
  useFormikContext,
} from "formik";
import { debounce, set } from "lodash";
import {
  FilesIcon,
  InfoIcon,
  MailIcon,
  PlusIcon,
  TicketIcon,
} from "lucide-react";
import { act, useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react";

/* todo: 

ticket dropdown is more complicated than we first thought.
it not only has to be a multi select, but it also needs to make sure that we only allocate the number of tickets that are available.
probably needs to be it's own sub component in here

if we allow the Formik form to select tickets for each user, each time we render whats available to be picked, we essentially need
to show the total number of tickets available, minus the number of tickets already allocated to other users from the form values.


*/

const Cell = ({
  name,
  index,
  underline,
}: {
  name: string;
  index: number;
  underline?: boolean;
}) => {
  const { values, errors } = useFormikContext();
  const {
    manageAccreditationGroupCellOpen,
    setManageAccreditationGroupCellOpen,
  } = usePortalStore();
  const inputRef = useRef<HTMLInputElement>(null);

  const setGroupCellOpen = (field: string, idx: number) => {
    const cell = `${field}-${idx}`;
    if (manageAccreditationGroupCellOpen !== cell) {
      setManageAccreditationGroupCellOpen(cell);
    }
  };

  const fieldKey = `individuals[${index}].${name}`;
  const fieldError = getIn(errors, fieldKey);
  const value = getIn(values, fieldKey);

  useEffect(() => {
    if (
      manageAccreditationGroupCellOpen === `${name}-${index}` &&
      inputRef.current
    ) {
      inputRef.current.focus();
    }
  }, [manageAccreditationGroupCellOpen, name, index]);

  // When someone presses tab move to the next cell
  useEffect(() => {
    if (
      manageAccreditationGroupCellOpen === `${name}-${index}` &&
      inputRef.current
    ) {
      inputRef.current.focus();
    }
  }, [manageAccreditationGroupCellOpen, name, index]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
      const fields = ["firstName", "lastName", "email"];
      const currentIndex = fields.indexOf(name);
      const nextIndex = (currentIndex + 1) % fields.length;
      const nextField = fields[nextIndex];
      setGroupCellOpen(nextField, index);
    }
  };

  return (
    <td
      className={`${
        fieldError ? "bg-red-400/10 text-red-400" : ""
      } relative whitespace-nowrap py-2 pl-1 pr-4 text-sm font-medium cursor-pointer`}
      onClick={() => setGroupCellOpen(name, index)}
    >
      {manageAccreditationGroupCellOpen === `${name}-${index}` ? (
        <div className="absolute inset-0 w-full h-full rounded-md shadow-lg">
          <Field
            innerRef={inputRef}
            style={{ paddingLeft: "0.65rem", letterSpacing: "-0.01rem" }}
            onKeyDown={handleKeyDown}
            className="w-full h-full bg-transparent -none dark:bg-zinc-800 text-sm pt-[0.4rem]"
            name={`individuals[${index}].${name}`}
          />
        </div>
      ) : (
        <div
          className={`pl-2 ${!value ? "py-2.5" : ""} ${
            underline ? "underline decoration-zinc-700" : ""
          }`}
        >
          {getIn(values, `individuals[${index}].${name}`)}
        </div>
      )}
      {fieldError && (
        <div className="absolute top-0 right-0 translate-y-1/2 mr-2 mt-0.5">
          <ExclamationCircleIcon className="text-red-400 w-4 h-4" />
        </div>
      )}
    </td>
  );
};

const fetchGroup = async (id: string): Promise<ManageAccreditationGroup> => {
  const { data } = await axios.get(
    `${BASE_URL}/accreditation/groups/${id}/manage`
  );
  return data;
};

export const updateGroupRequest = async (
  id: string,
  group: ManageAccreditationGroupRequest
): Promise<ManageAccreditationGroup> => {
  const { data } = await axios.put(
    `${BASE_URL}/accreditation/groups/${id}/manage`,
    group
  );
  return data;
};

interface IndividualRowProps {
  individual: AccreditationIndividual;
  index: number;
  ticketCount: Record<number, number>;
  groupAllocatedTickets: Array<TicketAllocation>;
  deleteIndividual: (uuid: string) => void;
  sendEmail: (individualId: number) => void;
  performingActionToIndividual: number | null;
  isDarkMode: boolean;
}

const IndividualRow = ({
  individual,
  index,
  ticketCount,
  groupAllocatedTickets,
  deleteIndividual,
  sendEmail,
  performingActionToIndividual,
  isDarkMode,
}: IndividualRowProps) => {
  const { errors } = useFormikContext();
  const fieldErrors = getIn(errors, `individuals[${index}]`);

  return (
    <tr
      key={index}
      className="divide-x divide-gray-200 dark:divide-white/10 dark:text-gray-200 text-gray-500"
    >
      <Field type="text" name={`individuals[${index}].id`} hidden />
      <Field type="text" name={`individuals[${index}].id`} hidden />
      <Cell name="firstName" index={index} />
      <Cell name="lastName" index={index} />
      <Cell name="email" index={index} underline />
      <td
        className={`${
          fieldErrors?.tickets ? "bg-red-400/10 text-red-400" : ""
        } relative whitespace-nowrap`}
      >
        <TicketAllocationDropdown
          name={`individuals[${index}].tickets`}
          tickets={groupAllocatedTickets}
          ticketCount={ticketCount}
          hideBorder
        />
        {fieldErrors?.tickets && (
          <div className="flex items-center pl-1">
            <ExclamationCircleIcon className="w-4 h-4 mr-1 mt-0.5" />
            <div>
              <span className="text-xs">{fieldErrors.tickets}</span>
            </div>
          </div>
        )}
      </td>
      <td className="whitespace-nowrap py-1 pl-2 pr-4 text-sm sm:pr-0 w-16">
        {performingActionToIndividual === index ? (
          <div className="flex items-center justify-center">
            <ButtonSpinner colour={isDarkMode ? "white" : "black"} />
          </div>
        ) : performingActionToIndividual ? (
          <></>
        ) : individual.isGroupContact ? (
          <Badge variant="secondary">Group contact</Badge>
        ) : (
          <div>
            <span
              onClick={() => deleteIndividual(individual.uuid)}
              className="mr-1.5 cursor-pointer inline-flex items-center rounded-md bg-gray-400/10 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-400/20"
            >
              <TrashIcon className="h-4 w-4" />
            </span>
            {/* <span
              onClick={() => sendEmail(individual.id)}
              className="cursor-pointer inline-flex items-center rounded-md bg-gray-400/10 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-400/20"
            >
              <MailIcon className="h-4 w-4" />
            </span> */}
          </div>
        )}
      </td>
    </tr>
  );
};

export const individualsValidationSchema = yup.object().shape({
  individuals: yup.array().of(
    yup.object().shape({
      id: yup.number(),
      firstName: yup.string().required("First name is required"),
      lastName: yup.string().required("Last name is required"),
      email: yup
        .string()
        .email("Must be a valid email")
        .required("Email is required")
        .test(
          "unique-email",
          "Duplicate email addresses are not allowed",
          function (value) {
            const { individuals } = this.options.context;
            const duplicates = individuals.filter((i) => i.email === value);
            return duplicates.length <= 1;
          }
        ),
      tickets: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.number(),
            value: yup.string().required("Required"),
          })
        )
        .min(1, "At least one ticket must be selected"), // Ensure at least one ticket is selected
    })
  ),
});

// To extend the base schema, use .shape() on the inner object:
export const individualsValidationSchemaWithUuid =
  individualsValidationSchema.shape({
    individuals: yup.array().of(
      yup.object().shape({
        group: yup.number(),
        uuid: yup.string().required("UUID is required"),
        // ...other fields are inherited from the base schema
      })
    ),
  });

export const ManageGroup = () => {
  const queryClient = useQueryClient();
  const {
    manageAccreditationGroupCellOpen,
    setManageAccreditationGroupCellOpen,
  } = usePortalStore();
  const { groupId } = useParams<{ groupId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [performingActionToIndividual, setPerformingActionToIndividual] =
    useState<null | number>(null);
  const isDarkMode = useDarkMode();
  const [isSaving, setIsSaving] = useState(false);

  // query to get the data
  const { data, error, isLoading, isError } = useQuery(
    ["group", groupId],
    () => fetchGroup(groupId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // query to mutate the data
  const mutation = useMutation(
    ({ id, group }: { id: string; group: ManageAccreditationGroupRequest }) =>
      updateGroupRequest(id, group),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["group", groupId]);
        toast.success("Group updated successfully");
      },
      onError: (error) => {
        toast.error("Error saving group changes");
        Sentry.captureException(error);
        setIsSaving(false);
      },
      onSettled: () => {
        setIsSaving(false);
      },
    }
  );

  const deleteMutation = useMutation(
    (uuid: string) =>
      axios.delete(
        `${BASE_URL}/accreditation/groups/${groupId}/individuals/${uuid}`
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["group", groupId]);
        setPerformingActionToIndividual(null);
        // todo: success toast
        toast.success("♻️ Individual deleted successfully");
      },
      onError: () => {
        setPerformingActionToIndividual(null);
        toast.error("❌ Error deleting individual");
      },
    }
  );

  const updateGroup = async (data: ManageAccreditationGroupRequest) => {
    if (groupId) {
      mutation.mutate({ id: groupId, group: data });
    }
  };

  const deleteIndividual = (uuid: string) => {
    deleteMutation.mutate(uuid);
  };

  // const emailMutation = useMutation(
  //   (individualId: number) =>
  //     axios.post(`${BASE_URL}/accreditation/groups/${groupId}/email`, {
  //       individuals: [individualId],
  //     }),
  //   {
  //     onSuccess: () => {
  //       queryClient.invalidateQueries(["group", groupId]);
  //       setPerformingActionToIndividual(null);
  //       toast.success("📨 Email sent successfully");
  //     },
  //     onError: () => {
  //       toast.error("❌ Error sending email");
  //     },
  //   }
  // );

  const setGroupCellOpen = (field: string, index: number) => {
    const cell = `${field}-${index}`;
    setManageAccreditationGroupCellOpen(cell);
  };

  // When someone clicks outside the table, unfocus all
  const handleClickOutside = (event: MouseEvent) => {
    if (!(event.target as HTMLElement).closest("td")) {
      setManageAccreditationGroupCellOpen("");
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const submit = (values: FormikValues) => {
    // Check if any row has firstName, lastName, email, and at least one ticket
    const validRows = values.individuals.filter(
      (individual: AccreditationIndividual) =>
        individual.firstName !== "" &&
        individual.lastName !== "" &&
        individual.email !== "" &&
        individual.tickets?.length > 0 // Ensure at least one ticket is selected
    );

    const individuals = validRows.map(
      (individual: AccreditationIndividual) => ({
        ...individual,
        group: data?.id,
      })
    );

    if (validRows.length > 0) {
      updateGroup({ individuals });
    }
  };

  // const sendEmail = (individualId: number) => {
  //   emailMutation.mutate(individualId);
  // };

  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <AccreditationPortalLayout>
      <div className="dark:bg-dark-primary text-gray-200 font-sans min-h-screen">
        <div className="dark:bg-dark-secondary dark:border-white/10 py-3 border-b">
          <div className="max-w-5xl mx-auto flex lg:px-0 px-3">
            <div className="ml-2">
              <Logo height={22} />
            </div>
            <div className="flex-1 justify-end flex mr-2">
              <ManageGroupHelpDrawer />
            </div>
          </div>
        </div>
        {data && (
          <div className="max-w-5xl mx-auto dark:bg-dark-secondary bg-white rounded-b-lg border-x border-b dark:border-white/10 p-4">
            <div className="">
              <Formik
                initialValues={{ individuals: data.individuals }}
                validationSchema={individualsValidationSchema}
                onSubmit={(values: FormikValues) => {
                  setIsSaving(true);
                  submit(values);
                }}
              >
                {({ values, handleSubmit, errors, validateForm }) => {
                  const isDuplicateEmailError = errors?.individuals?.some(
                    (individual: AccreditationIndividual) =>
                      individual?.email &&
                      individual?.email.includes("Duplicate email")
                  );

                  // use the formik values to get a list of all the tickets that have already been picked
                  const allPickedTickets =
                    values.individuals?.reduce((acc, individual) => {
                      return acc.concat(individual.tickets || []);
                    }, []) || [];

                  // Count each picked ticket by ID
                  const ticketCount = allPickedTickets.reduce((acc, ticket) => {
                    acc[ticket.id] = (acc[ticket.id] || 0) + 1;
                    return acc;
                  }, {});

                  return (
                    <form>
                      <div className="flow-root">
                        <div className="sm:-mx-6 lg:-mx-8 ">
                          <div className="align-middle sm:px-6 lg:px-8">
                            <Tabs
                              defaultValue="tickets"
                              className="w-full relative"
                            >
                              <div className="lg:flex">
                                <div>
                                  <div className="flex">
                                    <div className="mb-2 pl-2 -mt-6 flex w-full relative">
                                      <h3 className="scroll-m-20 text-4xl font-semibold tracking-tight pt-6 pb-1 -ml-1 dark:text-white text-gray-900">
                                        {data.name}
                                      </h3>
                                    </div>
                                  </div>
                                  <div className="flex items-center -mb-1">
                                    <TabsList>
                                      <TabsTrigger value="tickets">
                                        Tickets
                                      </TabsTrigger>
                                      {data.canUploadDocuments && (
                                        <TabsTrigger value="documents">
                                          Documents
                                        </TabsTrigger>
                                      )}
                                    </TabsList>
                                  </div>
                                </div>
                                <div className="lg:flex-1 lg:justify-end flex lg:mt-0 mt-4">
                                  <Card className="p-2 w-[20rem] self-start">
                                    <CardHeader className="p-0 pb-1">
                                      <CardTitle className="text-sm">
                                        Allocated tickets
                                      </CardTitle>
                                    </CardHeader>
                                    {data.groupAllocatedTickets.length > 0 ? (
                                      data.groupAllocatedTickets.map(
                                        (ticket) => {
                                          const pickedCount =
                                            ticketCount[ticket.ticket.id] || 0;
                                          const availableQuantity =
                                            ticket.quantity - pickedCount;
                                          return (
                                            <div
                                              key={ticket.ticket.id}
                                              className="text-gray-400 text-xs flex"
                                            >
                                              <div className="font-semibold">
                                                {ticket.ticket.name}
                                              </div>
                                              <div className="flex flex-1 justify-end">
                                                {availableQuantity} /{" "}
                                                {ticket.quantity}
                                              </div>
                                            </div>
                                          );
                                        }
                                      )
                                    ) : (
                                      <span className="text-sm">
                                        This group does not have any tickets
                                        allocated yet
                                      </span>
                                    )}
                                  </Card>
                                </div>
                              </div>
                              <TabsContent value="tickets" className="">
                                <Alert className="mt-4">
                                  <InfoIcon className="h-4 w-4" />
                                  <AlertTitle>Heads up!</AlertTitle>
                                  <AlertDescription>
                                    <span className="hidden lg:inline">
                                      On the right hand side{" "}
                                    </span>
                                    <span className="lg:hidden lg:block">
                                      Above{" "}
                                    </span>
                                    you have a list of all of the allocated
                                    tickets you can assign. Don't worry, we'll
                                    send out your passes for you closer to the
                                    event with arrival information.
                                    <br />
                                    You don't have to save your details, they
                                    will save automatically! For more
                                    information, click "Need help?" above.
                                  </AlertDescription>
                                </Alert>

                                {isDuplicateEmailError && (
                                  <Alert
                                    className="mt-2 pb-3"
                                    variant="destructive"
                                  >
                                    <InfoIcon className="h-4 w-4" />
                                    <AlertTitle>
                                      Duplicate email address found, each
                                      individual must have their own email
                                    </AlertTitle>
                                  </Alert>
                                )}

                                <div className="flex flex-1 justify-end my-2 bg-red-500">
                                  <div className="inline-block w-1/2"></div>
                                </div>
                                <table className="table min-w-full divide-y divide-gray-300 dark:divide-white/10 hidden lg:table">
                                  <thead>
                                    <tr className="divide-x border-t dark:border-white/10 divide-gray-200 dark:divide-white/10 text-gray-900 dark:text-gray-500">
                                      <th
                                        scope="col"
                                        className="px-2.5 py-2 text-left text-sm font-medium w-28 whitespace-nowrap"
                                      >
                                        First Name
                                      </th>
                                      <th
                                        scope="col"
                                        className="px-2.5 py-2 text-left text-sm font-medium w-28 whitespace-nowrap"
                                      >
                                        Last Name
                                      </th>
                                      <th
                                        scope="col"
                                        className="px-2.5 py-2 text-left text-sm  font-medium w-60 whitespace-nowrap"
                                      >
                                        Email
                                      </th>
                                      <th
                                        scope="col"
                                        className="py-2 pl-2.5 pr-4 text-left text-sm sm:pr-0 font-medium whitespace-nowrap"
                                      >
                                        Tickets
                                      </th>
                                      <th
                                        scope="col"
                                        className="py-2 pl-4 pr-4 text-left text-sm sm:pr-0"
                                      ></th>
                                    </tr>
                                  </thead>
                                  <FieldArray
                                    name="individuals"
                                    render={(arrayHelpers) => (
                                      <tbody className="divide-y divide-gray-200 bg-white dark:divide-white/10 dark:bg-transparent">
                                        {values.individuals.map(
                                          (
                                            individual: AccreditationIndividual,
                                            index: number
                                          ) => (
                                            <IndividualRow
                                              key={index}
                                              individual={individual}
                                              index={index}
                                              ticketCount={ticketCount}
                                              groupAllocatedTickets={
                                                data.groupAllocatedTickets
                                              }
                                              deleteIndividual={(uuid) => {
                                                setPerformingActionToIndividual(
                                                  index
                                                );
                                                deleteIndividual(uuid);
                                                arrayHelpers.remove(index);
                                              }}
                                              sendEmail={() => {
                                                setPerformingActionToIndividual(
                                                  index
                                                );
                                                sendEmail(individual.id);
                                              }}
                                              performingActionToIndividual={
                                                performingActionToIndividual
                                              }
                                              isDarkMode={isDarkMode}
                                            />
                                          )
                                        )}

                                        <tr
                                          key="addUser"
                                          className="dark:text-gray-200 text-gray-500 hover:dark:bg-zinc-800/40"
                                          onClick={() => {
                                            // push the new person to the array
                                            arrayHelpers.push({
                                              uuid: uuidv4(),
                                              firstName: "",
                                              lastName: "",
                                              email: "",
                                              role: "",
                                            });

                                            // set the first cell open
                                            setGroupCellOpen(
                                              "firstName",
                                              values.individuals.length
                                            );
                                          }}
                                        >
                                          <td
                                            colSpan={6}
                                            className="dark:text-gray-500 border-b dark:border-white/10"
                                          >
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              type="button"
                                            >
                                              <PlusIcon className="mr-1" /> Add
                                              person
                                            </Button>
                                          </td>
                                        </tr>
                                      </tbody>
                                    )}
                                  />
                                </table>
                                <Button
                                  size="sm"
                                  type="button"
                                  onClick={() => handleSubmit()}
                                  className="lg:mt-4"
                                >
                                  {isSaving ? (
                                    <div className="flex items-center">
                                      <ButtonSpinner
                                        colour={isDarkMode ? "black" : "white"}
                                      />
                                      Saving
                                    </div>
                                  ) : (
                                    "Save changes"
                                  )}
                                </Button>
                                {/* mobile card based version */}
                                <div className="lg:hidden mt-2 space-y-4">
                                  <FieldArray
                                    name="individuals"
                                    render={(arrayHelpers) => (
                                      <>
                                        <Button
                                          type="button"
                                          onClick={() => {
                                            // push the new person to the array
                                            arrayHelpers.push({
                                              uuid: uuidv4(),
                                              firstName: "",
                                              lastName: "",
                                              email: "",
                                              role: "",
                                            });

                                            // set the first cell open
                                            setGroupCellOpen(
                                              "firstName",
                                              values.individuals.length
                                            );

                                            setTimeout(() => {
                                              handleScroll();
                                            }, 250);
                                          }}
                                        >
                                          Add person
                                        </Button>
                                        {values.individuals.map(
                                          (
                                            individual: AccreditationIndividual,
                                            index: number
                                          ) => {
                                            const fieldErrors = getIn(
                                              errors,
                                              `individuals[${index}]`
                                            );
                                            return (
                                              <div
                                                key={index}
                                                ref={
                                                  index ===
                                                  values.individuals.length - 1
                                                    ? scrollRef
                                                    : null
                                                }
                                                className="rounded-lg border dark:border-white/10 p-4 bg-white dark:bg-zinc-900 shadow-sm"
                                              >
                                                <div className="-mx-4 -mt-4 border-b mb-4 py-1 px-3 flex justify-between items-center">
                                                  <div className="text-zinc-500 text-sm">
                                                    Person #{index + 1}
                                                  </div>
                                                  <div className="mb-1">
                                                    {individual.isGroupContact ? (
                                                      <Badge
                                                        variant="secondary"
                                                        className=""
                                                      >
                                                        Group contact
                                                      </Badge>
                                                    ) : (
                                                      <button
                                                        type="button"
                                                        onClick={() => {
                                                          setPerformingActionToIndividual(
                                                            index
                                                          );
                                                          deleteIndividual(
                                                            individual.uuid
                                                          );
                                                          arrayHelpers.remove(
                                                            index
                                                          );
                                                        }}
                                                        className="text-sm text-red-500 hover:underline"
                                                      >
                                                        Remove
                                                      </button>
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                  <div className="flex items-center space-x-3 flex-1 mb-1 text-zinc-800 dark:text-white">
                                                    <Avatar className="h-8 w-8 bg-zinc-500">
                                                      <AvatarFallback>
                                                        {individual.firstName
                                                          ?.charAt(0)
                                                          .toUpperCase()}
                                                        {individual.lastName
                                                          ?.charAt(0)
                                                          .toUpperCase()}
                                                      </AvatarFallback>
                                                    </Avatar>
                                                    <div className="text-sm font-medium dark:text-white flex-1 flex">
                                                      <div className="mr-1">
                                                        <Input
                                                          type="text"
                                                          required
                                                          name={`individuals[${index}].firstName`}
                                                          value={
                                                            values.individuals[
                                                              index
                                                            ].firstName
                                                          }
                                                        />
                                                      </div>
                                                      <div className="ml-1">
                                                        <Input
                                                          type="text"
                                                          required
                                                          name={`individuals[${index}].lastName`}
                                                          value={
                                                            values.individuals[
                                                              index
                                                            ].lastName
                                                          }
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                                <div className="mt-2 text-sm text-gray-500 dark:text-gray-300">
                                                  <div>
                                                    <div className="mb-2">
                                                      Email
                                                    </div>
                                                    <Input
                                                      type="text"
                                                      required
                                                      name={`individuals[${index}].email`}
                                                      value={
                                                        values.individuals[
                                                          index
                                                        ].email
                                                      }
                                                    />
                                                  </div>
                                                  <div className="mt-3">
                                                    <div className="mb-2">
                                                      Tickets
                                                    </div>
                                                    <TicketAllocationDropdown
                                                      name={`individuals[${index}].tickets`}
                                                      tickets={
                                                        data.groupAllocatedTickets
                                                      }
                                                      ticketCount={ticketCount}
                                                    />
                                                  </div>
                                                  {fieldErrors?.tickets && (
                                                    <div className="flex items-center pl-1 text-red-400 mt-1">
                                                      <ExclamationCircleIcon className="w-4 h-4 mr-1 mt-0.5" />
                                                      <div>
                                                        <span className="text-xs">
                                                          {fieldErrors.tickets}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          }
                                        )}
                                      </>
                                    )}
                                  />
                                </div>
                              </TabsContent>
                              {data.canUploadDocuments && (
                                <TabsContent value="documents">
                                  <div className="relative">
                                    <Documents groupId={data.uuid} />
                                  </div>
                                </TabsContent>
                              )}
                            </Tabs>
                          </div>
                        </div>
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </div>
          </div>
        )}
      </div>
    </AccreditationPortalLayout>
  );
};
